import { INTERNAL_NOTE } from 'constants/messageTypes';
import axios from 'axios';
import draftToHtml from 'draftjs-to-html';
import { ContentState, convertToRaw, EditorState, Modifier } from 'draft-js';
import htmlToDraft from 'html-to-draftjs';
import get from 'lodash/get';
import environment from '../environment';
import { getSessionTokenHeader } from './util';
import { handleError } from 'services/ErrorService/ErrorService';

export const DEFAULT_DEBOUNCE_AMOUNT_FOR_GET_PREDICTION_TEXT = 300;

const cookPredictionTextHtml = (newPredictionText) => `<span style="color: grey">${newPredictionText}</span>`;

export const cookConversationMessagesForPredictionAPI = (currentConversation) => {
  return currentConversation.allMessages
    .map((id) => {
      if (currentConversation[id].messageType !== INTERNAL_NOTE) {
        return {
          messageBody: currentConversation[id].messageBody,
          source: currentConversation[id].source,
          direction: currentConversation[id].direction,
        };
      }

      return null;
    })
    .filter(Boolean);
};

export const checkIfPredictionIsTurnedOn = (allowPredictionTextConfig, currentExpertId, currentPartnerId) => {
  const partnerIsInConfig = (allowPredictionTextConfig || []).find((partner) => partner.partnerId === currentPartnerId);
  const expertsConnectedToPartner = get(partnerIsInConfig, 'expertsIds', []);
  const expertIsInConfig = expertsConnectedToPartner.includes(currentExpertId);

  return !!(currentPartnerId && currentExpertId && partnerIsInConfig && expertIsInConfig);
};

export const getPredictionTextViaApi = async (conversationId, businessId, prefix, messages) => {
  const requestParams = {
    conversationId,
    businessId,
    prefix,
    messages,
  };
  try {
    const sessionTokenHeader = await getSessionTokenHeader();
    const requestConfig = { headers: sessionTokenHeader };
    const response = await axios.post(environment.services.autoCompletePredictionMLUrl, requestParams, requestConfig);

    return await response.data;
  } catch (error) {
    handleError({ error, customMessage: 'Prediction API error' });
  }
};

export const removePredictionText = (predictionText, editorState) => {
  const selectionState = editorState.getSelection();
  const focusOffset = selectionState.getFocusOffset();
  const currentSelection = editorState.getSelection().merge({ focusOffset });
  const contentWithoutOldPredictionText = removePredictionTextFromContents(predictionText, editorState);
  let newEditorState = EditorState.push(editorState, contentWithoutOldPredictionText, 'insert-fragment');
  newEditorState = EditorState.forceSelection(newEditorState, currentSelection);

  return newEditorState;
};

export const removePredictionTextFromContents = (predictionText, editorState) => {
  const predictionTextLength = predictionText.length;
  const currentContent = editorState.getCurrentContent();
  const lastBlockLength = currentContent.getBlockMap().last().getLength();
  const anchorOffset = lastBlockLength - predictionTextLength;
  const focusOffset = lastBlockLength;
  const focusKey = currentContent.getBlockMap().last().getKey();
  const anchorKey = focusKey;
  const selectionState = editorState.getSelection();
  const selectionOfOldPredictionText = selectionState.merge({ focusKey, anchorKey, anchorOffset, focusOffset });
  const contentWithoutOldPredictionText = Modifier.replaceText(currentContent, selectionOfOldPredictionText, '');

  return contentWithoutOldPredictionText;
};

export const appendPredictionTextToContents = (oldPredictionText, newPredictionText, editorState) => {
  const focusOffset = editorState.getSelection().getFocusOffset();
  const contentWithoutOldPredictionText =
    oldPredictionText.length > 0
      ? removePredictionTextFromContents(oldPredictionText, editorState)
      : editorState.getCurrentContent();
  const rawContent = convertToRaw(contentWithoutOldPredictionText);
  const currentHtml = draftToHtml(rawContent);
  const newHtml = cookPredictionTextHtml(newPredictionText);
  const { contentBlocks, entityMap } = htmlToDraft(currentHtml + newHtml);
  const newContent = ContentState.createFromBlockArray(contentBlocks, entityMap);
  let newEditorState = EditorState.push(editorState, newContent, 'insert-fragment');
  const selectionState = newEditorState.getSelection();
  const newSelection = selectionState.merge({
    focusKey: newEditorState.getCurrentContent().getBlockMap().last().getKey(),
    anchorKey: newEditorState.getCurrentContent().getBlockMap().last().getKey(),
    anchorOffset: focusOffset,
    focusOffset: focusOffset,
  });
  newEditorState = EditorState.forceSelection(newEditorState, newSelection);

  return newEditorState;
};

export const editorAllowUndoTurnOff = (editorState) => EditorState.set(editorState, { allowUndo: false });

export const editorAllowUndoTurnOn = (editorState) => EditorState.set(editorState, { allowUndo: true });

export const checkFocusOnLastBlockEnd = (predictionText, editorState) => {
  if (!editorState.getCurrentContent().hasText()) {
    return true;
  }
  const predictionTextLength = predictionText.length;
  const currentContent = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  const anchorKey = selectionState.getAnchorKey();
  const currentBlock = currentContent.getBlockForKey(anchorKey);
  const lastBlockKey = currentContent.getBlockMap().last().getKey();
  const focusOffset = editorState.getSelection().getFocusOffset();
  const anchorOffset = currentBlock.getLength() - predictionTextLength;

  return currentBlock.getKey() === lastBlockKey && focusOffset === anchorOffset;
};
